import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import Tween from "@tweenjs/tween.js";
import { throttle } from "lodash";

const BuiltinShaderAttributeName = [
  "position",
  "normal",
  "uv",
  "color",
  "skinIndex",
  "skinWeight",
  "instanceMatrix",
  "morphTarget0",
  "morphTarget1",
  "morphTarget2",
  "morphTarget3",
  "morphTarget4",
  "morphTarget5",
  "morphTarget6",
  "morphTarget7",
  "morphNormal0",
  "morphNormal1",
  "morphNormal2",
  "morphNormal3",
];

function VerticesDuplicateRemove(arr) {
  const set = new Set();
  let t = [];
  for (let i = 0; i < arr.length; i++) {
    t.push(arr[i]);
    if (i % 3 === 2) {
      set.add(t.join(","));
      t = [];
    }
  }
  const res = [];
  set.forEach((val) => {
    res.push(...val.split(",").map((val) => Number(val)));
  });
  return new Float32Array(res);
}

export class AddonsBasic {
  constructor() {
    this.Geometry = undefined;
    this.update = undefined;
    this.ChangeModel = undefined;
  }
}

function getRangeRandom(e, t) {
  return Math.random() * (t - e) + e;
}

export class ModularParticleSystem extends THREE.Group {
  constructor(options) {
    super();

    const {
      AnimateDuration = 1500,
      onModelsFinishedLoad,
      LoadingManager,
      particleTextureUrl,
      addons = [],
      showDebug = false,
      autoStart = true,
      models = [],
      enableMouseFollowX = true,
      enableMouseFollowY = true,
    } = options;

    // 配置属性
    this.particleTextureUrl = particleTextureUrl;
    this.addons = addons;
    this.Models = new Map();
    this.AnimateDuration = AnimateDuration;
    this.onModelsFinishedLoad = onModelsFinishedLoad;
    this.manager = LoadingManager || new THREE.LoadingManager();
    this.defaultLoader = new OBJLoader(this.manager);
    this.showDebug = showDebug;
    this.autoStart = autoStart;

    // 内部状态
    this.ParticleAnimeMap = [];
    this.modelList = new Map();
    this.maxParticlesCount = 0;
    this.MainParticleGroup = null;
    this.CurrentUseModelName = undefined;
    this.LastUseModelName = undefined;
    this.PointMaterial = null;
    this.AnimateEffectParticle = null;

    // 鼠标控制
    this.mouseK = 0;
    this.mouseV = 0;
    this.hadListenMouseMove = false;
    this.WIDTH = window.innerWidth;
    this.HEIGHT = window.innerHeight;
    this.enableMouseFollowX = enableMouseFollowX;
    this.enableMouseFollowY = enableMouseFollowY;

    // 将 addons 添加到这个 group
    this.addons.forEach((val) => {
      if (val.Geometry) {
        this.add(val.Geometry);
      }
    });

    // 初始化时添加传入的模型
    models.forEach((modelConfig) => {
      this.addModel(modelConfig);
    });

    // 绑定方法
    this.rotateScene = throttle(this._rotateScene.bind(this), 100);

    // 如果自动开始，则初始化
    if (autoStart) {
      this._initialize();
    }
  }

  // 初始化方法，可以手动调用
  initialize() {
    return this._initialize();
  }

  _initialize() {
    this.createPointMaterial();

    // 如果没有模型，直接完成初始化
    if (this.Models.size === 0) {
      console.warn(
        "No models added to particle system. Please add models using addModel() method.",
      );
      this._finishLoadModal();
      return Promise.resolve();
    }

    return this._addModels();
  }

  async _addModels() {
    const modelPromises = [];

    this.Models.forEach((modelConfig, name) => {
      const promise = new Promise((resolve, reject) => {
        try {
          this._loadModel(modelConfig, resolve);
        } catch (error) {
          console.error(`Error loading model ${name}:`, error);
          reject(error);
        }
      });
      modelPromises.push(promise);
    });

    try {
      await Promise.all(modelPromises);
      this._finishLoadModal();
    } catch (error) {
      console.error("Error loading models:", error);
      // 即使有模型加载失败，也尝试继续
      if (this.Models.size > 0) {
        this._finishLoadModal();
      }
    }
  }

  _loadModel(modelConfig, onComplete) {
    let finalGeometry,
      finalVertices = new Float32Array([]);

    const finishLoad = () => {
      if (!finalGeometry) {
        console.error(`Failed to load geometry for model ${modelConfig.name}`);
        onComplete();
        return;
      }

      // 确保有position属性
      if (!finalGeometry.attributes.position) {
        console.error(`Model ${modelConfig.name} has no position attribute`);
        finalGeometry.setAttribute(
          "position",
          new THREE.Float32BufferAttribute([], 3),
        );
      }

      // 应用缩放
      if (modelConfig.scale) {
        if (typeof modelConfig.scale === "number") {
          finalGeometry.scale(
            modelConfig.scale,
            modelConfig.scale,
            modelConfig.scale,
          );
        } else if (
          Array.isArray(modelConfig.scale) &&
          modelConfig.scale.length === 3
        ) {
          finalGeometry.scale(
            modelConfig.scale[0],
            modelConfig.scale[1],
            modelConfig.scale[2],
          );
        }
      }

      // 应用平移
      if (modelConfig.translate) {
        finalGeometry.translate(
          modelConfig.translate.x || 0,
          modelConfig.translate.y || 0,
          modelConfig.translate.z || 0,
        );
      }

      // 应用旋转（可选）
      if (modelConfig.rotate) {
        const rotation = modelConfig.rotate;
        const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(
          new THREE.Euler(rotation.x || 0, rotation.y || 0, rotation.z || 0),
        );
        finalGeometry.applyMatrix4(rotationMatrix);
      }

      this.modelList.set(modelConfig.name, finalGeometry);

      // 回调
      if (modelConfig.onLoadComplete) {
        modelConfig.onLoadComplete(finalGeometry);
      }

      if (this.showDebug) {
        const positions = finalGeometry.attributes.position.array;
        if (positions && positions.length > 0) {
          let minX = Infinity,
            maxX = -Infinity;
          let minY = Infinity,
            maxY = -Infinity;
          let minZ = Infinity,
            maxZ = -Infinity;

          for (let j = 0; j < positions.length; j += 3) {
            minX = Math.min(minX, positions[j]);
            maxX = Math.max(maxX, positions[j]);
            minY = Math.min(minY, positions[j + 1]);
            maxY = Math.max(maxY, positions[j + 1]);
            minZ = Math.min(minZ, positions[j + 2]);
            maxZ = Math.max(maxZ, positions[j + 2]);
          }

          console.log(`Model ${modelConfig.name} loaded:`, {
            vertices: finalGeometry.attributes.position.count,
            bounds: {
              x: [minX.toFixed(2), maxX.toFixed(2)],
              y: [minY.toFixed(2), maxY.toFixed(2)],
              z: [minZ.toFixed(2), maxZ.toFixed(2)],
            },
          });
        } else {
          console.log(`Model ${modelConfig.name} loaded with 0 vertices`);
        }
      }

      onComplete();
    };

    if (modelConfig.loader) {
      modelConfig.loader.loaderInstance.load(
        modelConfig.path,
        (obj) => {
          finalGeometry = modelConfig.loader.load(obj);
          finishLoad();
        },
        undefined,
        (error) => {
          console.error(`Error loading model ${modelConfig.name}:`, error);
          finishLoad();
        },
      );
    } else if (modelConfig.path) {
      this.defaultLoader.load(
        modelConfig.path,
        (group) => {
          for (const child of group.children) {
            const arr = child.geometry.attributes.position.array;
            finalVertices = new Float32Array([...finalVertices, ...arr]);
          }
          if (!(modelConfig.NeedRemoveDuplicateParticle === false)) {
            finalVertices = VerticesDuplicateRemove(finalVertices);
          }
          finalGeometry = new THREE.BufferGeometry();
          finalGeometry.setAttribute(
            "position",
            new THREE.BufferAttribute(finalVertices, 3),
          );
          modelConfig.geometry = finalGeometry;
          finishLoad();
        },
        undefined,
        (error) => {
          console.error(`Error loading model ${modelConfig.name}:`, error);
          finishLoad();
        },
      );
    } else if (modelConfig.geometry instanceof THREE.BufferGeometry) {
      finalGeometry = modelConfig.geometry;
      finishLoad();
    } else if (modelConfig.geometry) {
      // 处理可能传入的非 BufferGeometry
      try {
        if (
          modelConfig.geometry.attributes &&
          modelConfig.geometry.attributes.position
        ) {
          // 已经是 BufferGeometry 但被错误判断
          finalGeometry = modelConfig.geometry;
        } else {
          // 尝试转换为 BufferGeometry
          finalGeometry = new THREE.BufferGeometry();
          finalGeometry.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(
              modelConfig.geometry.attributes
                ? modelConfig.geometry.attributes.position.array
                : [],
              3,
            ),
          );
        }
      } catch (error) {
        console.error(
          `Error processing geometry for model ${modelConfig.name}:`,
          error,
        );
      }
      finishLoad();
    } else {
      console.error(`No valid geometry source for model ${modelConfig.name}`);
      finishLoad();
    }
  }

  _finishLoadModal() {
    let maxParticlesCount = 0;
    let hasValidModels = false;

    this.modelList.forEach((geometry, name) => {
      if (
        geometry.attributes.position &&
        geometry.attributes.position.count > 0
      ) {
        const count = geometry.attributes.position.count;
        maxParticlesCount = Math.max(maxParticlesCount, count);
        hasValidModels = true;

        if (this.showDebug) {
          console.log(`Model ${name} vertices: ${count}`);
        }
      } else {
        console.warn(`Model ${name} has no vertices or position attribute`);
      }
    });

    // 如果没有有效的模型，创建一些默认粒子
    if (!hasValidModels) {
      console.warn("No valid models found. Creating default particle system.");
      maxParticlesCount = 1000; // 默认粒子数量
    }

    this.maxParticlesCount = maxParticlesCount;

    if (this.showDebug) {
      console.log(`Max particles: ${maxParticlesCount}`);
    }

    const vertices = [];
    const randMaxLength = 1500;
    this.MainParticleGroup = new Tween.Group();

    for (let i = 0; i < maxParticlesCount; i++) {
      const x = getRangeRandom(-randMaxLength, randMaxLength);
      const y = getRangeRandom(-randMaxLength, randMaxLength);
      const z = getRangeRandom(-randMaxLength, randMaxLength);
      vertices.push(x, y, z);

      const p = {
        x,
        y,
        z,
        isPlaying: true,
        index: i,
      };

      p.tweenctx = new Tween.Tween(p, this.MainParticleGroup)
        .easing(Tween.Easing.Quadratic.Out)
        .onComplete((o) => {
          o.isPlaying = false;
        })
        .onStart((o) => {
          o.isPlaying = true;
        });

      this.ParticleAnimeMap[i] = p;
    }

    const AnimateEffectGeometry = new THREE.BufferGeometry();
    AnimateEffectGeometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(vertices, 3),
    );
    this.AnimateEffectParticle = new THREE.Points(
      AnimateEffectGeometry,
      this.PointMaterial,
    );
    this.add(this.AnimateEffectParticle);

    if (this.showDebug) {
      console.log("Particle system initialized");
    }

    if (this.onModelsFinishedLoad) {
      this.onModelsFinishedLoad(this.AnimateEffectParticle, this);
    }
  }

  createPointMaterial() {
    const TextureLoader = new THREE.TextureLoader();
    let texture = null;

    if (this.particleTextureUrl) {
      try {
        texture = TextureLoader.load(this.particleTextureUrl);
      } catch (error) {
        console.error("Error loading particle texture:", error);
      }
    }

    this.PointMaterial = new THREE.PointsMaterial({
      size: 0.05,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      map: texture,
      vertexColors: false,
    });
  }

  // 添加模型到系统
  addModel(modelConfig) {
    if (!modelConfig.name) {
      console.error("Model config must have a name");
      return;
    }

    this.Models.set(modelConfig.name, modelConfig);

    // 如果已经初始化，则重新加载模型
    if (this.AnimateEffectParticle) {
      this._loadModel(modelConfig, () => {
        console.log(`Model ${modelConfig.name} added after initialization`);

        // 如果这是第一个模型，自动切换到它
        if (this.Models.size === 1 && !this.CurrentUseModelName) {
          this.changeModel(modelConfig.name);
        }
      });
    }
  }

  // 切换模型
  changeModel(name, time = this.AnimateDuration) {
    const item = this.modelList.get(name);
    if (!item) {
      console.warn(
        `Model not found: ${name}. Available models:`,
        Array.from(this.modelList.keys()),
      );
      return;
    }

    const modelConfig = this.Models.get(name);
    if (!modelConfig) {
      console.warn(`Model config not found: ${name}`);
      return;
    }

    this.LastUseModelName = this.CurrentUseModelName;
    this.CurrentUseModelName = name;

    if (modelConfig.onEnterStart) {
      modelConfig.onEnterStart(this.AnimateEffectParticle);
    }

    const targetModel = item.attributes.position;
    const sourceModel = this.AnimateEffectParticle.geometry.attributes.position;
    const targetCount = targetModel.count;

    if (this.showDebug) {
      console.log(`Switching to model ${name}:`, {
        targetVertices: targetCount,
        particles: this.maxParticlesCount,
      });
    }

    // 创建目标位置数组
    const targetPositions = [];
    for (let i = 0; i < this.maxParticlesCount; i++) {
      const targetIndex = targetCount > 0 ? i % targetCount : 0;
      targetPositions.push({
        x: targetModel.array[targetIndex * 3] || 0,
        y: targetModel.array[targetIndex * 3 + 1] || 0,
        z: targetModel.array[targetIndex * 3 + 2] || 0,
      });
    }

    // 添加随机延迟
    const baseDelay = time * 0.3;

    for (let i = 0; i < this.maxParticlesCount; i++) {
      const p = this.ParticleAnimeMap[i];
      if (!p) continue;

      const targetPos = targetPositions[i];

      p.tweenctx
        .stop()
        .to(
          {
            x: targetPos.x,
            y: targetPos.y,
            z: targetPos.z,
          },
          time,
        )
        .delay(Math.random() * baseDelay)
        .easing(Tween.Easing.Quadratic.Out)
        .onUpdate((o) => {
          sourceModel.setXYZ(i, o.x, o.y, o.z);
          sourceModel.needsUpdate = true;
        })
        .start();
    }

    // 设置回调超时
    setTimeout(() => {
      if (modelConfig.onEnterEnd) {
        modelConfig.onEnterEnd(this.AnimateEffectParticle);
      }

      if (this.showDebug) {
        console.log(`Model ${name} transition complete`);
      }
    }, time + baseDelay);

    // 调用 addons 的 ChangeModel 钩子
    this.addons.forEach((val) => {
      if (val.ChangeModel) {
        val.ChangeModel(this);
      }
    });
  }

  // 开始监听鼠标移动
  listenMouseMove(width = window.innerWidth, height = window.innerHeight) {
    if (!this.hadListenMouseMove) {
      this.WIDTH = width;
      this.HEIGHT = height;
      window.addEventListener("mousemove", this.rotateScene);
      this.hadListenMouseMove = true;
    }
  }

  // 停止监听鼠标移动
  stopListenMouseMove() {
    if (this.hadListenMouseMove) {
      window.removeEventListener("mousemove", this.rotateScene);
      this.hadListenMouseMove = false;
    }
  }

  // 重置视角
  alignToCenter(immediately = false) {
    if (immediately) {
      this.rotation.x = 0;
      this.rotation.y = 0;
      this.mouseV = 0;
      this.mouseK = 0;
      return;
    }

    const e = new MouseEvent("click", {
      clientX: this.WIDTH / 2,
      clientY: this.HEIGHT / 2,
    });
    this.rotateScene(e);
  }

  _rotateScene(e) {
    // 根据配置决定是否更新相应轴的值
    if (this.enableMouseFollowY) {
      this.mouseV = 0.0001 * (e.clientX - this.WIDTH / 2);
    }

    if (this.enableMouseFollowX) {
      this.mouseK = 0.0001 * (e.clientY - this.HEIGHT / 2);
    }
  }

  _updateRotation() {
    this.rotation.y += (this.mouseV - this.rotation.y) / 50;
    this.rotation.x += (this.mouseK - this.rotation.x) / 50;
  }

  // 更新方法，需要在主渲染循环中调用
  update(deltaTime) {
    Tween.update();

    if (this.MainParticleGroup) {
      this.MainParticleGroup.update();
    }

    this._updateRotation();

    // 更新当前模型的动画帧
    if (this.CurrentUseModelName) {
      const modelConfig = this.Models.get(this.CurrentUseModelName);
      if (modelConfig && modelConfig.onAnimationFrameUpdate) {
        const needsUpdate = modelConfig.onAnimationFrameUpdate(
          this.AnimateEffectParticle,
          this.ParticleAnimeMap,
          modelConfig.geometry,
        );

        if (needsUpdate === true) {
          for (const attrName of BuiltinShaderAttributeName) {
            const attribute =
              this.AnimateEffectParticle?.geometry?.getAttribute(attrName);
            if (attribute) {
              attribute.needsUpdate = true;
            }
          }
        }
      }
    }

    // 更新 addons
    this.addons.forEach((val) => {
      if (val.update) {
        val.update();
      }
    });
  }

  // 清理资源
  dispose() {
    this.stopListenMouseMove();

    if (this.AnimateEffectParticle) {
      this.AnimateEffectParticle.geometry.dispose();
      this.AnimateEffectParticle.material.dispose();
      this.remove(this.AnimateEffectParticle);
    }

    this.addons.forEach((val) => {
      if (val.Geometry) {
        val.Geometry.dispose();
      }
    });

    this.ParticleAnimeMap = [];
    this.modelList.clear();
    this.Models.clear();
  }

  // 获取当前状态
  getStats() {
    return {
      currentModel: this.CurrentUseModelName,
      particleCount: this.maxParticlesCount,
      models: Array.from(this.Models.keys()),
      loadedModels: Array.from(this.modelList.keys()),
      mouseControl: this.hadListenMouseMove,
    };
  }
}

export default ModularParticleSystem;
